import React from 'react';

interface Props {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('Uncaught error:', error, info.componentStack);
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      return (
        <div style={styles.page}>
          <div style={styles.card}>
            <div style={styles.icon}>⚠️</div>
            <h2 style={styles.title}>Something went wrong</h2>
            <p style={styles.message}>
              {this.state.error?.message || 'An unexpected error occurred.'}
            </p>
            <button
              style={styles.button}
              onClick={() => {
                this.setState({ hasError: false, error: null });
                window.location.href = '/dashboard';
              }}
            >
              Return to Dashboard
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Design-system tokens, read via var(). This component wraps the whole app —
// including the auth screens — so it must not carry the old brown palette.
const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'var(--bg-page)',
    fontFamily: 'var(--font-ui)',
    padding: 'var(--sp-6)',
  },
  card: {
    background: 'var(--bg-surface)',
    border: '1px solid var(--border-default)',
    borderRadius: 'var(--r-xl)',
    padding: 'var(--sp-12) var(--sp-10)',
    maxWidth: 480, width: '100%',
    textAlign: 'center',
    boxShadow: 'var(--shadow-4)',
  },
  icon: { fontSize: 48, marginBottom: 'var(--sp-4)' },
  title: {
    margin: '0 0 var(--sp-3)',
    fontFamily: 'var(--font-display)',
    fontSize: 'var(--text-h1)', lineHeight: 'var(--lh-h1)',
    fontWeight: 600, letterSpacing: '-0.02em',
    color: 'var(--text-primary)',
  },
  message: {
    color: 'var(--text-secondary)',
    fontSize: 'var(--text-body-lg)', lineHeight: 'var(--lh-body-lg)',
    marginBottom: 'var(--sp-8)', wordBreak: 'break-word',
  },
  button: {
    background: 'var(--gradient-primary)',
    color: 'var(--text-on-brand)',
    border: 'none',
    borderRadius: 'var(--r-md)',
    minHeight: 'var(--btn-h-lg)',
    padding: '0 var(--sp-6)',
    fontFamily: 'var(--font-ui)',
    fontSize: 'var(--text-body-lg)', fontWeight: 600,
    cursor: 'pointer',
    boxShadow: 'var(--glow)',
  },
};

export default ErrorBoundary;
